/**
 * useAchievements · achievement engine wired to ProgressContext.
 *
 * Auto-evaluates on every progress change. Newly earned badges trigger toast
 * notifications via the shared toast bus. Progress-bar updates are committed
 * for X/Y locked badges.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useProgress } from '@/contexts/ProgressContext';
import { evaluate, type EngineContext } from '@shared/engine/achievements.engine';
import { BADGE_CATALOG } from '@shared/engine/badge-catalog';
import type { EarnedBadge, BadgeDefinition, Scenario } from '@/types';
import { getPracticeStore } from '@/lib/practiceStore';
import { GLOSSARY } from '@shared/data/glossary';
import { SCENARIOS } from '@/data/scenarios';
import { MINI_LESSONS_META } from '@/data/miniLessonsCatalog';
import { QUIZ_PACKS_META } from '@/data/quizPacksCatalog';

/**
 * Per Genie ack 1 MAY 2026 (Option a) for `atelier-resident-s04/s05/s06`:
 * a sprint counts a bonus engagement when ≥1 of these is present:
 *   - any Bonus Practice task scenario completed
 *   - any related-sprint mini-lesson marked read
 *   - any related-sprint quiz pack solved (any score >= 50%)
 */
function computeBonusSprintEngagement(
  bonusCompletedScenarioIds: Set<string>,
  miniLessonsReadAt: Record<string, number>,
  quizPacksSolved: Record<string, { scorePct: number }>,
): Record<number, boolean> {
  const out: Record<number, boolean> = {};
  const flag = (sprint: number) => {
    out[sprint] = true;
  };
  // Bonus Practice tasks completed.
  for (const s of SCENARIOS as ReadonlyArray<Scenario>) {
    if (s.tier !== 'bonus') continue;
    if (bonusCompletedScenarioIds.has(s.id)) flag(s.sprint);
  }
  // Mini-lessons read (any).
  for (const ml of MINI_LESSONS_META) {
    if (miniLessonsReadAt[ml.id]) flag(ml.relatedSprint);
  }
  // Quiz packs solved with at least a passing score.
  for (const qp of QUIZ_PACKS_META) {
    const r = quizPacksSolved[qp.id];
    if (r && r.scorePct >= 0.5) flag(qp.relatedSprint);
  }
  return out;
}

type ToastCallback = (badge: BadgeDefinition) => void;
const toastListeners = new Set<ToastCallback>();

export function subscribeBadgeToast(cb: ToastCallback): () => void {
  toastListeners.add(cb);
  return () => toastListeners.delete(cb);
}

function emitBadgeToast(badge: BadgeDefinition) {
  toastListeners.forEach((cb) => {
    try {
      cb(badge);
    } catch {
      /* listener error — drop */
    }
  });
}

export function useAchievements() {
  const { progress, recordEarnedBadge, setAchievementProgress } = useProgress();

  const evaluating = useRef(false);

  const runEvaluation = useCallback(async () => {
    if (evaluating.current) return;
    evaluating.current = true;
    try {
      const practiceStats = await getPracticeStore().getRankStats();
      const allPractice = await getPracticeStore().getAllProgress();
      const completedScenarios = new Set(
        allPractice.filter((p) => p.completedAt).map((p) => p.scenarioId),
      );
      const bonusScenariosCompleted = (SCENARIOS as ReadonlyArray<Scenario>).filter(
        (s) => s.tier === 'bonus' && completedScenarios.has(s.id),
      ).length;

      const masteredCount = Object.values(progress.cockpit.flashcards).filter(
        (s) => (s.reps ?? 0) >= 1,
      ).length;
      const glossaryMasteredCount = Math.min(masteredCount, GLOSSARY.length);

      const bonusSprintEngagement = computeBonusSprintEngagement(
        completedScenarios,
        progress.achievements.miniLessons.readAt,
        progress.achievements.miniLessons.quizPacksSolved,
      );

      const ctx: EngineContext = {
        progress,
        practiceStats,
        bonusScenariosCompleted,
        glossaryMasteredCount,
        bonusSprintEngagement,
        block1Capstone: progress.capstone['block-1'],
        // W3.1 extension 2026-05-04 · Block 2 + Block 3 release-unlock support.
        block2Capstone: progress.capstone['block-2'],
        block3Capstone: progress.capstone['block-3'],
        // W4.1 extension 2026-05-04 · capability flags for Tier 4 curiosity triggers
        // (Bug Hunter B2 · Prompt Injector · Bias Detector · etc · 11 badges total).
        capabilityFlags: progress.achievements.capabilityFlags,
      };

      const { newlyEarned, progressUpdates } = evaluate(progress.achievements, ctx);

      for (const badge of newlyEarned) {
        const earned: EarnedBadge = {
          badgeId: badge.id,
          earnedAt: Date.now(),
        };
        recordEarnedBadge(earned);
        emitBadgeToast(badge);
      }

      for (const [badgeId, value] of Object.entries(progressUpdates)) {
        setAchievementProgress(badgeId, value);
      }
    } finally {
      evaluating.current = false;
    }
  }, [progress, recordEarnedBadge, setAchievementProgress]);

  // Throttled auto-evaluation: wait 200ms after the last progress mutation.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      void runEvaluation();
    }, 200);
    return () => window.clearTimeout(handle);
  }, [runEvaluation]);

  const earnedBadgeIds = new Set(progress.achievements.earned.map((e) => e.badgeId));

  return {
    state: progress.achievements,
    badges: BADGE_CATALOG,
    earnedBadgeIds,
    evaluate: runEvaluation,
  };
}
